import { apiFetch } from './api';

export interface FlowStep {
  kind: 'text' | 'image' | 'video' | 'file';
  text: string | null;
  media_asset_id: string | null;
}

export interface FlowButton {
  id: string;
  parent_button_id: string | null;
  label: string;
  action_kind: 'text' | 'image' | 'video' | 'file' | 'url';
  text: string | null;
  media_asset_id: string | null;
  url: string | null;
}

export interface FlowSummary {
  id: string;
  kind: string;
  target_media_id: string | null;
  match_type: string | null;
  keywords: string[];
  reply_text: string | null;
  enabled: boolean;
  priority: number;
  expires_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface FlowDetail {
  flow: FlowSummary;
  steps: FlowStep[];
  buttons: FlowButton[];
}

export interface StoryItem {
  media_id: string;
  caption: string | null;
  media_type: string | null;
  thumbnail_url: string | null;
  media_url: string | null;
  permalink: string | null;
  story_timestamp: number | null;
  expires_at: number;
}

export interface MediaItem {
  media_id: string;
  caption: string | null;
  media_type: string | null;
  thumbnail_url: string | null;
  permalink: string | null;
  timestamp: number | null;
}

export interface HighlightCollection {
  id: string;
  name: string;
  created_at: number;
  updated_at: number;
  story_media_ids: string[];
}

export interface MediaAsset {
  id: string;
  kind: 'image' | 'video' | 'file';
  mime_type: string;
  size_bytes: number;
  original_name: string | null;
  created_at: number;
}

const SECTION_PATHS = {
  story: '/api/stories',
  post: '/api/posts',
  highlight: '/api/highlights/flows',
  highlight_story: '/api/highlights/stories',
  dm: '/api/dm-rules',
} as const;

export type SectionKind = keyof typeof SECTION_PATHS;

export async function listFlows(kind: SectionKind, accountId: string): Promise<FlowSummary[]> {
  const data = await apiFetch<{ flows: FlowSummary[] }>(
    `${SECTION_PATHS[kind]}?account_id=${encodeURIComponent(accountId)}`,
  );
  return data.flows;
}

export async function getFlow(kind: SectionKind, id: string): Promise<FlowDetail> {
  return apiFetch<FlowDetail>(`${SECTION_PATHS[kind]}/${encodeURIComponent(id)}`);
}

export interface FlowCreatePayload {
  instagram_account_id: string;
  target_media_id?: string | null;
  match_type?: string | null;
  keywords?: string[] | null;
  reply_text?: string | null;
  enabled?: boolean;
  priority?: number;
  steps?: Array<{ kind: string; text?: string | null; media_asset_id?: string | null }>;
  buttons?: Array<{
    label: string;
    action_kind: string;
    text?: string | null;
    media_asset_id?: string | null;
    url?: string | null;
  }>;
}

export async function createFlow(
  kind: SectionKind,
  payload: FlowCreatePayload,
): Promise<FlowDetail> {
  return apiFetch<FlowDetail>(SECTION_PATHS[kind], {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateFlow(
  kind: SectionKind,
  id: string,
  payload: Partial<FlowCreatePayload>,
): Promise<FlowDetail> {
  return apiFetch<FlowDetail>(`${SECTION_PATHS[kind]}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function setFlowEnabled(
  kind: SectionKind,
  id: string,
  enabled: boolean,
): Promise<void> {
  await apiFetch<{ enabled: boolean }>(
    `${SECTION_PATHS[kind]}/${encodeURIComponent(id)}/${enabled ? 'enable' : 'disable'}`,
    { method: 'POST' },
  );
}

export async function deleteFlow(kind: SectionKind, id: string): Promise<void> {
  await apiFetch<{ deleted: boolean }>(`${SECTION_PATHS[kind]}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function refreshStories(accountId: string): Promise<StoryItem[]> {
  const data = await apiFetch<{ stories: StoryItem[] }>(
    `/api/stories/refresh?account_id=${encodeURIComponent(accountId)}`,
  );
  return data.stories;
}

export async function refreshPosts(accountId: string): Promise<MediaItem[]> {
  const data = await apiFetch<{ media: MediaItem[] }>(
    `/api/posts/refresh?account_id=${encodeURIComponent(accountId)}`,
  );
  return data.media;
}

export async function listHighlightCollections(accountId: string): Promise<HighlightCollection[]> {
  const data = await apiFetch<{ collections: HighlightCollection[] }>(
    `/api/highlights/collections?account_id=${encodeURIComponent(accountId)}`,
  );
  return data.collections;
}

export async function createHighlightCollection(
  accountId: string,
  name: string,
  storyMediaIds: string[],
): Promise<HighlightCollection> {
  return apiFetch<HighlightCollection>('/api/highlights/collections', {
    method: 'POST',
    body: JSON.stringify({ instagram_account_id: accountId, name, story_media_ids: storyMediaIds }),
  });
}

export async function deleteHighlightCollection(id: string): Promise<void> {
  await apiFetch<{ deleted: boolean }>(`/api/highlights/collections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function addHighlightCollectionStory(id: string, storyMediaId: string): Promise<void> {
  await apiFetch<{ added: boolean }>(
    `/api/highlights/collections/${encodeURIComponent(id)}/stories`,
    {
      method: 'POST',
      body: JSON.stringify({ story_media_id: storyMediaId }),
    },
  );
}

export async function removeHighlightCollectionStory(
  id: string,
  storyMediaId: string,
): Promise<void> {
  await apiFetch<{ removed: boolean }>(
    `/api/highlights/collections/${encodeURIComponent(id)}/stories/${encodeURIComponent(storyMediaId)}`,
    { method: 'DELETE' },
  );
}

export async function listMediaAssets(kind?: string): Promise<MediaAsset[]> {
  const qs = kind !== undefined ? `?kind=${encodeURIComponent(kind)}` : '';
  const data = await apiFetch<{ assets: MediaAsset[] }>(`/api/media${qs}`);
  return data.assets;
}

export async function uploadMediaAsset(accountId: string, file: File): Promise<MediaAsset> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`/api/media/upload?account_id=${encodeURIComponent(accountId)}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(payload?.message ?? 'Upload failed');
  }
  const data = (await response.json()) as { asset: MediaAsset };
  return data.asset;
}

export async function deleteMediaAsset(id: string): Promise<void> {
  await apiFetch<{ deleted: boolean }>(`/api/media/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function disconnectAccount(id: string): Promise<void> {
  await apiFetch<{ disconnected: boolean }>(
    `/api/instagram/accounts/${encodeURIComponent(id)}/disconnect`,
    {
      method: 'POST',
    },
  );
}
